import { useState, type ReactNode } from "react";
import { Check, Copy, Code2, Image as ImageIcon, Plug } from "lucide-react";

/**
 * Backlink magnet: collapse/expand container that generates an embeddable
 * HTML snippet pointing at DrHint with a visible do-follow credit
 * anchor. Three variants:
 *   - widget: iframe + credit line (gold-rate / weather dashboards)
 *   - chart:  anchor-wrapped image + credit line (news / blog graphs)
 *   - api:    plain attribution credit snippet (developer hub)
 */
export type BacklinkVariant = "widget" | "chart" | "api";

export function BacklinkEmbed({
  variant,
  targetUrl,
  embedSrc,
  imageSrc,
  imageAlt,
  title,
  ctaLabel,
  width = 640,
  height = 250,
  preview,
  defaultOpen = false,
}: {
  variant: BacklinkVariant;
  targetUrl: string;
  embedSrc?: string;
  imageSrc?: string;
  imageAlt?: string;
  title: string;
  ctaLabel?: string;
  width?: number;
  height?: number;
  preview?: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const snippet = buildSnippet({ variant, targetUrl, embedSrc, imageSrc, imageAlt, width, height });
  const Icon = variant === "chart" ? ImageIcon : variant === "api" ? Code2 : Plug;

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked */
    }
  }

  return (
    <section
      className="mt-8 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-card/40 to-background overflow-hidden shadow-sm"
      aria-label={title}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-amber-500/5 transition"
      >
        <span className="flex items-center gap-2 text-sm md:text-base font-bold">
          <Icon className="h-4 w-4 text-amber-500" /> {title}
        </span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : ctaLabel ?? "Show code"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            Paste this snippet into any blog, news article or website. It updates automatically and
            includes a visible source credit linking back to DrHint.
          </p>

          {preview && (
            <div className="rounded-xl border border-border bg-card/60 p-3">
              <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Live preview</div>
              {preview}
            </div>
          )}

          <div className="relative">
            <pre className="overflow-x-auto rounded-lg border border-border p-3 pr-24 text-[11px] leading-relaxed max-h-56" style={{ background: "#0b0d18", color: "#e9ecf5" }}>
              <code className="font-mono whitespace-pre-wrap break-all" style={{ color: "#e9ecf5" }}>{snippet}</code>
            </pre>
            <button
              type="button"
              onClick={copy}
              className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-bold hover:opacity-90" style={{ color: "#12131f" }}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy Embed Code"}
            </button>
          </div>

          <p className="text-[10px] text-muted-foreground">
            Free for personal and commercial use. Attribution link must remain visible.
          </p>
        </div>
      )}
    </section>
  );
}

function buildSnippet(args: {
  variant: BacklinkVariant;
  targetUrl: string;
  embedSrc?: string;
  imageSrc?: string;
  imageAlt?: string;
  width: number;
  height: number;
}): string {
  const credit = (label: string) =>
    `<div style="text-align:right;font-size:11px;margin-top:5px;font-family:Arial,sans-serif;">Data Source: <a href="${args.targetUrl}" target="_blank" rel="noopener">${label}</a></div>`;

  if (args.variant === "widget") {
    return [
      `<iframe src="${args.embedSrc ?? args.targetUrl}" width="100%" height="${args.height}" frameborder="0" loading="lazy" title="DrHint Live Dashboard"></iframe>`,
      credit("DrHint Realtime Portal"),
    ].join("");
  }

  if (args.variant === "chart") {
    const img = args.imageSrc ?? "https://drhint.com/icon-512.png";
    const alt = args.imageAlt ?? "Live financial chart by DrHint";
    return [
      `<a href="${args.targetUrl}" target="_blank" rel="noopener" title="${alt}">`,
      `<img src="${img}" alt="${alt}" style="max-width:100%;height:auto;border:0;border-radius:8px;" loading="lazy" />`,
      `</a>`,
      credit("DrHint Live Chart"),
    ].join("");
  }

  // api
  return `<p style="font-size:12px;font-family:Arial,sans-serif;">Live data streaming via <a href="${args.targetUrl}" target="_blank" rel="noopener">DrHint API</a></p>`;
}